// Shared strategy-implementation modal: embeds the endpoint's Flow diagram
// (the kind's implementation) in an iframe pointed at the bare /flow-embed
// route, plus an "Open in Flow" link to the full studio.
//
// WHY an iframe instead of navigating to /flow: the embedded Flow studio's
// WebSocket tears down whenever its route unmounts, so navigating cortex ->
// /flow -> back -> /flow re-handshakes + re-authenticates each time, and the
// endpoint's data RPC races that fresh auth (canvas intermittently empty on
// repeat visits). The iframe is its own persistent document — opening/closing
// the modal never churns the parent app's connection, so it loads reliably.
import React from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";

export default function StrategyImplModal({ title, impl, flowProjectId, onClose }) {
  const embedHref = flowProjectId && impl
    ? `/flow-embed/projects/${flowProjectId}/endpoints/${impl}/flow`
    : null;
  const flowHref = flowProjectId && impl
    ? `/flow/projects/${flowProjectId}/endpoints/${impl}/flow`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      data-testid="strategy-impl-modal"
    >
      <div
        className="bg-card-bg rounded-[10px] border border-card-border w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-card-border">
          <h2 className="text-base font-medium text-page-title">
            {title || "Implementation in Flow"}
          </h2>
          <div className="flex items-center gap-4">
            {flowHref && (
              <Link to={flowHref} data-testid="strategy-open-in-flow"
                className="inline-flex items-center gap-1.5 text-xs text-nav-active hover:text-nav-active-hover no-underline">
                <ExternalLink size={13} /> Open in Flow
                <code className="text-[11px] text-page-muted ml-1">{impl}</code>
              </Link>
            )}
            <button onClick={onClose} className="text-page-muted hover:text-page-text border-0 bg-transparent cursor-pointer p-1" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-[var(--page-bg)]">
          {embedHref ? (
            <iframe
              data-testid="strategy-flow-iframe"
              src={embedHref}
              title={`Flow implementation: ${impl}`}
              className="w-full h-full border-0"
              style={{ minHeight: 480 }}
            />
          ) : (
            <div className="p-6 text-page-muted text-sm">No Flow implementation linked for this transformation.</div>
          )}
        </div>
      </div>
    </div>
  );
}
